export const politiqueConfidentialiteContent = `# Politique de confidentialité
Dernière mise à jour : juillet 2026

### 1. Responsable du traitement
La présente Politique de confidentialité décrit la manière dont les données personnelles sont collectées, utilisées et protégées sur la plateforme JobVinum, accessible notamment à l'adresse : https://jobvinum.io

Le responsable du traitement est :

ETOH SAS
Société par Actions Simplifiée au capital de 51 000 €
9 Rue des Colonnes
75002 Paris
France

SIREN : 828 397 745
TVA : FR91828397745

Présidente : Audrey Sousa Chaillet

Contact : hello@jobvinum.io

### 2. Définitions
* **Plateforme** : Le site internet et l'application JobVinum.
* **Données personnelles** : Toute information permettant d'identifier directement ou indirectement une personne physique.
* **Utilisateur** : Toute personne utilisant JobVinum.
* **Candidat** : Toute personne créant un profil afin de rechercher un emploi.
* **Employeur** : Toute entreprise ou exploitation viticole utilisant JobVinum pour recruter.

### 3. Champ d'application
Cette Politique explique comment JobVinum collecte, utilise, conserve et protège les données personnelles. En utilisant la plateforme, l'utilisateur reconnaît avoir pris connaissance de cette Politique. ETOH SAS peut la modifier à tout moment afin de tenir compte des évolutions légales, réglementaires ou techniques.

### 4. Données collectées
Nous collectons uniquement les données nécessaires au fonctionnement de JobVinum.

**Pour tous les utilisateurs** : nom, prénom, adresse email, téléphone, langue, pays, mot de passe chiffré.

**Pour les candidats** : photographie, nationalité, adresse, disponibilités, langues parlées, compétences, expériences professionnelles, CV, documents d'identité, permis de conduire, RIB (facultatif), numéro de sécurité sociale (facultatif), documents nécessaires au recrutement.

**Pour les employeurs** : raison sociale, SIRET, coordonnées, adresse, coordonnées du contact, offres d'emploi, historique des recrutements, historique des paiements.

**Données techniques** : lors de l'utilisation de JobVinum, certaines informations techniques sont collectées automatiquement : adresse IP, navigateur, appareil, système d'exploitation, journaux techniques, données de connexion.

### 5. Finalités du traitement
Les données sont utilisées notamment pour : créer les comptes utilisateurs ; gérer les profils ; publier les offres d'emploi ; mettre en relation employeurs et candidats ; permettre les candidatures ; gérer les paiements ; assurer le support utilisateur ; sécuriser la plateforme ; améliorer les fonctionnalités ; produire des statistiques anonymisées.

Nous ne vendons jamais les données personnelles des utilisateurs.

### 6. Bases légales
Les traitements reposent notamment sur : l'exécution du contrat ; le respect d'obligations légales ; l'intérêt légitime d'ETOH SAS ; le consentement lorsque celui-ci est requis.

### 7. Paiements
Les paiements sont réalisés via Stripe. ETOH SAS ne conserve jamais les numéros de cartes bancaires. Les données de paiement sont directement traitées par Stripe conformément aux normes PCI-DSS.

### 8. Communications
**Emails transactionnels** : création de compte, validation d'email, candidatures, confirmations de paiement, notifications liées aux recrutements. Ces communications sont nécessaires au fonctionnement du service.

**Communications marketing** : les newsletters et offres commerciales ne sont envoyées qu'après consentement explicite. Le consentement peut être retiré à tout moment.

### 9. Partage des données
Les données personnelles ne sont jamais revendues. Certaines informations sont partagées uniquement dans le cadre du fonctionnement de JobVinum. Par exemple, le candidat qui postule à une offre accepte que son dossier soit transmis à l'employeur concerné. Les coordonnées complètes d'un candidat ne sont accessibles qu'aux employeurs ayant débloqué son profil conformément aux fonctionnalités de la plateforme.

### 10. Prestataires
Nous travaillons notamment avec : Railway (hébergement de l'application et de la base de données, UE/USA) ; Netlify (hébergement du site web) ; Cloudflare R2 (stockage des documents et photos) ; Stripe (paiement) ; Google Gemini API (recherche assistée par intelligence artificielle) ; Resend (emails transactionnels) ; Google Fonts (polices de caractères, chargées depuis les serveurs de Google).

Ces prestataires agissent uniquement pour notre compte et dans le cadre de contrats garantissant la protection de vos données.

### 11. Transferts internationaux
Certaines données peuvent être traitées en dehors de l'Union européenne. Ces transferts sont réalisés conformément au RGPD et reposent notamment sur : les Clauses Contractuelles Types de la Commission européenne ; toute autre garantie reconnue par la réglementation applicable.

### 12. Durée de conservation
À titre indicatif : comptes utilisateurs : 2 ans après la dernière activité ; facturation : 10 ans ; données marketing : jusqu'au retrait du consentement ; documents administratifs : pendant la durée nécessaire au fonctionnement du service ou conformément aux obligations légales.

### 13. Sécurité
Nous mettons en œuvre des mesures techniques et organisationnelles afin de protéger les données personnelles, notamment : chiffrement des communications ; authentification sécurisée ; contrôle des accès ; sauvegardes ; journalisation ; surveillance de l'infrastructure.

### 14. Vos droits
Conformément au RGPD, vous disposez notamment des droits suivants : accès ; rectification ; effacement ; limitation ; opposition ; portabilité.

Vous pouvez exercer vos droits à tout moment à l'adresse : hello@jobvinum.io

Nous répondrons dans un délai maximal d'un mois.

### 15. Cookies
JobVinum n'utilise que des cookies strictement nécessaires au fonctionnement de la plateforme, notamment pour maintenir votre session de connexion (authentification sécurisée). Aucun cookie de mesure d'audience, de publicité ou de traçage n'est utilisé.

Conformément à l'article 82 de la loi Informatique et Libertés et à la réglementation ePrivacy, ces cookies strictement nécessaires ne requièrent pas de consentement préalable. Si cela venait à évoluer (ajout d'outils de mesure d'audience par exemple), un bandeau de consentement serait mis en place avant tout dépôt de cookie non essentiel.

### 16. Décisions automatisées
JobVinum peut utiliser des traitements automatisés afin de : proposer des candidats correspondant aux critères d'une offre ; afficher des offres pertinentes aux candidats ; améliorer la pertinence des résultats de recherche.

Ces traitements ont uniquement pour objectif d'améliorer l'expérience utilisateur. La décision finale de recrutement appartient toujours à l'employeur.

### 17. Protection des documents
Les documents déposés sur JobVinum (CV, pièces d'identité, permis, RIB, etc.) sont stockés de manière sécurisée. Ils ne sont accessibles qu'aux personnes autorisées dans le cadre du recrutement. Aucun document n'est rendu public.

### 18. Recrutement et visibilité des données
Afin de permettre la mise en relation entre employeurs et candidats, certaines données personnelles sont rendues accessibles aux utilisateurs de la plateforme selon leur rôle et leurs droits d'accès.

Les candidats autorisent JobVinum à afficher aux employeurs inscrits les informations nécessaires à l'évaluation de leur candidature, notamment : prénom ; photo de profil (si renseignée) ; nationalité ; langues parlées ; compétences ; expériences professionnelles ; disponibilités ; pays et régions de recherche ; tout autre élément du profil destiné au recrutement.

Les coordonnées personnelles (adresse e-mail, numéro de téléphone) ainsi que les documents administratifs (CV, pièce d'identité, permis de conduire, RIB, numéro de sécurité sociale ou tout autre document) ne sont accessibles qu'aux employeurs ayant obtenu cet accès via les fonctionnalités prévues par la plateforme, notamment après déblocage du dossier candidat.

Les candidats peuvent à tout moment modifier ou supprimer les informations de leur profil, sous réserve des obligations légales de conservation applicables.

### 19. Réclamations
Si vous estimez que vos droits ne sont pas respectés, vous pouvez contacter : hello@jobvinum.io

Vous pouvez également introduire une réclamation auprès de la CNIL : https://www.cnil.fr

### 20. Droit applicable
La présente Politique de confidentialité est régie par le droit français. Tout litige relatif à son interprétation ou à son exécution relève de la compétence des juridictions françaises compétentes.`;
